import React, { useMemo } from "react";
import { Card, CardContent, CardHeader } from "@/components/Card/Card";
import { DirectMailSpendData } from "../../api/fetchROIDashboardData/types";
import { MailIcon } from "lucide-react";
import { formatCurrency } from "@/modules/stochastic/features/Reports/SCDashboard/utils/chart";

interface Props {
  data?: DirectMailSpendData | null;
}

interface TransformedData {
  month: string;
  [key: string]: string | number;
}

export const DirectMailSpendTable: React.FC<Props> = ({ data }) => {
  const { transformedData, years, totals } = useMemo(() => {
    const items = data?.items || [];
    const backendTotals = data?.totals || {};

    const uniqueYears: string[] = Array.from(
      new Set(items.map((item) => item.year)),
    ).sort();

    // Define month order
    const monthOrder = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const monthlyData: { [month: string]: TransformedData } = {};

    monthOrder.forEach((month) => {
      monthlyData[month] = { month };
      uniqueYears.forEach((year) => {
        monthlyData[month][`${year}_count`] = 0;
        monthlyData[month][`${year}_spend`] = 0;
      });
    });

    items.forEach((item) => {
      if (monthlyData[item.month]) {
        monthlyData[item.month][`${item.year}_count`] = item.invoiceCount;
        monthlyData[item.month][`${item.year}_spend`] = item.invoiceTotal;
      }
    });

    const yearTotals: { [key: string]: number } = {};
    uniqueYears.forEach((year) => {
      const backendTotal = backendTotals[year];
      if (backendTotal) {
        yearTotals[`${year}_count`] = backendTotal.invoiceCount;
        yearTotals[`${year}_spend`] = backendTotal.invoiceTotal;
      } else {
        yearTotals[`${year}_count`] = 0;
        yearTotals[`${year}_spend`] = 0;
      }
    });

    return {
      transformedData: monthOrder.map((month) => monthlyData[month]),
      years: uniqueYears,
      totals: yearTotals,
    };
  }, [data]);

  const formatNumber = (value: number | string) => {
    const num = Number(value);
    if (num === 0) return "—";
    return num.toLocaleString();
  };

  const formatCurrencyValue = (value: number | string) => {
    const num = Number(value);
    if (num === 0) return "—";
    return formatCurrency(num);
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-lg bg-indigo-50 dark:bg-indigo-900/20">
            <MailIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Direct Mail Spend
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Monthly direct mail marketing expenditures
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pb-10">
        <div className="w-full overflow-auto border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm">
          <table className="w-full caption-bottom text-sm min-w-max">
            <thead className="[&_tr]:border-b">
              <tr className="bg-gray-50/75 dark:bg-gray-800/50">
                <th className="h-11 px-4 align-middle text-muted-foreground text-left border-r border-gray-200 dark:border-gray-700 bg-gray-50/75 dark:bg-gray-800/50 font-semibold uppercase text-xs tracking-wider">
                  Month
                </th>
                {years.map((year, index) => (
                  <React.Fragment key={year}>
                    <th className="h-11 px-4 align-middle text-muted-foreground text-center border-r border-gray-200 dark:border-gray-700 bg-gray-50/75 dark:bg-gray-800/50 whitespace-nowrap font-semibold uppercase text-xs tracking-wider">
                      {year} Invoices
                    </th>
                    <th
                      className={`h-11 px-4 align-middle text-muted-foreground text-center border-gray-200 dark:border-gray-700 bg-gray-50/75 dark:bg-gray-800/50 whitespace-nowrap font-semibold uppercase text-xs tracking-wider ${
                        index === years.length - 1 ? "" : "border-r"
                      }`}
                    >
                      {year} Spend
                    </th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody className="[&_tr:last-child]:border-0">
              {transformedData.map((row) => (
                <tr
                  className="border-b border-gray-200 dark:border-gray-700 transition-colors hover:bg-muted/50"
                  key={row.month}
                >
                  <td className="px-4 py-3 text-left text-gray-700 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700 whitespace-nowrap font-medium">
                    {row.month}
                  </td>
                  {years.map((year, index) => (
                    <React.Fragment key={year}>
                      <td className="px-4 py-3 text-right text-gray-900 dark:text-white whitespace-nowrap border-r border-gray-200 dark:border-gray-700">
                        {formatNumber(row[`${year}_count`])}
                      </td>
                      <td
                        className={`px-4 py-3 text-right text-gray-900 dark:text-white whitespace-nowrap ${
                          index === years.length - 1
                            ? ""
                            : "border-r border-gray-200 dark:border-gray-700"
                        }`}
                      >
                        {formatCurrencyValue(row[`${year}_spend`])}
                      </td>
                    </React.Fragment>
                  ))}
                </tr>
              ))}
              {/* Grand Total Row */}
              <tr className="bg-gray-50 dark:bg-gray-800/50 font-semibold">
                <td className="px-4 py-3 text-left text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-700 whitespace-nowrap">
                  Grand Total
                </td>
                {years.map((year, index) => (
                  <React.Fragment key={year}>
                    <td className="px-4 py-3 text-right text-gray-900 dark:text-white whitespace-nowrap border-r border-gray-200 dark:border-gray-700">
                      {formatNumber(totals[`${year}_count`])}
                    </td>
                    <td
                      className={`px-4 py-3 text-right text-gray-900 dark:text-white whitespace-nowrap ${
                        index === years.length - 1
                          ? ""
                          : "border-r border-gray-200 dark:border-gray-700"
                      }`}
                    >
                      {formatCurrencyValue(totals[`${year}_spend`])}
                    </td>
                  </React.Fragment>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
